import { RiArticleLine, RiSearchLine } from "@remixicon/react";
import classNames from "classnames";
import React from "react";
import {
    Article, ArtificialIntelligenceAnswerArticle, useGetAiAnswerLazyQuery, useListArticlesLazyQuery,
} from "../../../__generated__/api";
import { HtmlRenderer } from "../../../components/html-render/HtmlRenderer";
import Link from "../../../components/theme/button/Link";
import RouterLink from "../../../components/theme/button/RouterLink";
import FormGroup from "../../../components/theme/form-group/FormGroup";
import Icon from "../../../components/theme/icon/Icon";
import Text from "../../../components/theme/text/Text";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";
import usePortal from "../../../contexts/PortalContext";
import { Paths } from "../../../contexts/Routing";
import { useChangingDebounce } from "../../../debouncer";
import Styles from "./AnswerDetails.module.scss";

const LOADING_RESULTS: Partial<Article>[] = new Array(Math.floor(Math.random() * 6 + 2)).fill(0).map((__, index) => ({
    id: index,
    title: "title ".repeat(Math.random() * 5 + 2),
    previewText: "description ".repeat(Math.random() * 10),
}));

const LOADING_RESULTS_AI = {
    answer: {
        score: 1,
        answer: "ai answer ".repeat(Math.random() * 10 + 2),
    },
    articles: [] as ArtificialIntelligenceAnswerArticle[],
};

export default function SearchBar({
    setIsSearching,
    search,
    setSearch,
}: {
    setIsSearching?: (isSearching: boolean) => void;
    search: string | undefined;
    setSearch: (search: string) => void;
}) {
    const { isAIEnabled } = usePortal();
    const [doSearch, { loading: isLoading, data }] = useListArticlesLazyQuery();
    const [doGetAi, { loading: isLoadingAI, data: aiAnswer }] = useGetAiAnswerLazyQuery();

    const startSearch = useChangingDebounce(() => {
        if (search) {
            LoggingHandler.instance.metric([LoggingHandler.Metrics.ARTICLES, "search"]);
            doSearch({
                variables: {
                    query: search,
                    limit: 5,
                },
            });
            if (isAIEnabled) {
                doGetAi({
                    variables: {
                        search: {
                            query: search,
                        },
                    },
                });
            }
        }
    }, 500, [search]);

    const results = isLoading ? LOADING_RESULTS : data?.articles.results;
    const resultsAi = isLoadingAI ? LOADING_RESULTS_AI : aiAnswer?.artificialIntelligenceAnswer;

    return (
        <>
            <div className={Styles["search-bar"]}>
                <FormGroup
                    type="search"
                    size="filter"
                    placeholder="Search..."
                    prefixIcon={<RiSearchLine />}
                    onChange={evt => {
                        const query = evt.target.value;
                        setSearch(query);
                        setIsSearching?.(query != null && query !== "");
                        startSearch();
                    }}
                />
            </div>
            {search && (
                <div>
                    {resultsAi && (
                        <div className={classNames(Styles["ai-answer"], isLoadingAI ? "loading" : "")}>
                            <div className={Styles["ai-answer-header"]}>
                                <Icon.ArtificialIntelligence size="medium" variant="link" />
                                <span>
                                    AI Assist
                                </span>
                            </div>
                            <Text
                                className={Styles["ai-answer-text"]}
                                textVariant="body-1-regular"
                            >
                                {resultsAi.answer.answer}
                            </Text>
                            <div className={Styles["ai-answer-captions"]}>
                                {resultsAi.articles?.map(article => (
                                    article.url
                                        ? (
                                            <Link
                                                href={article.url}
                                                key={article.id}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                variant="secondary"
                                                size="xsmall"
                                                title={article.title || ""}
                                            >
                                                {article.previewText || article.title}
                                            </Link>
                                        )
                                        : (
                                            <RouterLink
                                                to={Paths.ANSWERS_GET}
                                                params={{ id: article.id }}
                                                key={article.id}
                                                variant="secondary"
                                                size="xsmall"
                                                title={article.title || ""}
                                            >
                                                {article.previewText || article.title}
                                            </RouterLink>
                                        )
                                ))}
                            </div>

                        </div>
                    )}
                    <ul className={classNames(Styles["search-results"], isLoading ? "loading" : "")}>
                        {results?.map(article => (
                            <RouterLink
                                key={article.id}
                                to={Paths.ANSWERS_GET}
                                params={{ id: article.id }}
                                search={{ search }}
                                className={Styles["search-result"]}
                                variant="none"
                                prefixIcon={<Icon icon={<RiArticleLine />} variant="tertiary" />}
                            >
                                <div>
                                    <Text className={Styles["search-title"]} textVariant="body-1-regular" colorVariant="body-primary">
                                        {article.title}
                                    </Text>
                                    <Text
                                        className={Styles["search-preview"]}
                                        textVariant="body-1-regular"
                                        colorVariant="body-secondary"
                                    >
                                        <HtmlRenderer
                                            html={article.previewText ?? ""}
                                            stripFormatting="search-results"
                                            shouldTrimBottom
                                        />
                                    </Text>
                                </div>
                            </RouterLink>
                        ))}
                    </ul>
                    {search && !isLoading && results?.isEmpty && (!resultsAi?.answer.answer || !isAIEnabled) && (
                        <div className={Styles["no-results"]}>
                            <Icon.Search size="large" variant="default" />
                            <Text
                                textVariant="body-1-regular"
                                colorVariant="body-secondary"
                            >
                                No articles match your search. Please try again.
                            </Text>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

SearchBar.defaultProps = {
    setIsSearching: undefined,
};
